import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { checkDevEnv } from '@stores/utils';
import { registerCacheClear } from './registry';
import type {
    CacheEntry,
    CacheStoreApi,
    CacheStoreOptions,
    OptionalParams,
} from './types';

export const FIVE_MINUTES_MS = 5 * 60 * 1000;
export const DEFAULT_MAX_ENTRIES = 50;

/**
 * Builds the cache key from the params array — the same stringification
 * useApiCall relies on to detect param changes.
 */
export const buildCacheKey = (params: unknown[]): string =>
    JSON.stringify(params);

/** Patch that may explicitly clear optional entry fields. */
type CacheEntryPatch<TData> = {
    [K in keyof CacheEntry<TData>]?: CacheEntry<TData>[K] | undefined;
};

interface CacheState<TData> {
    entries: Record<string, CacheEntry<TData>>;
}

interface CacheInternalActions<TData> {
    patchEntry: (key: string, patch: CacheEntryPatch<TData>) => void;
    removeEntry: (key: string) => void;
}

const EMPTY_ENTRY = Object.freeze({ isLoading: false }) as CacheEntry<never>;

const toErrorMessage = (err: unknown): string =>
    (err as Error)?.message ??
    'Something went wrong while executing the operation.';

/**
 * Creates a session-only Zustand cache store for one service read method.
 *
 * Full reference — lifecycle walkthroughs, diagrams, invariants:
 * docs/api-caching.md. Semantics in short:
 * - fresh hit  (< ttl): served synchronously, no network call;
 * - stale hit: stale data served synchronously, revalidated in the
 *   background (no loading state; data swaps in when the fetch resolves);
 * - miss: fetches with isLoading true until first data;
 * - refresh: explicit fetch, always bypasses the ttl window;
 * - errors never replace data and never update fetchedAt, so failed
 *   revalidations do not extend freshness.
 *
 * No persist middleware — cached data never survives a session.
 */
export const createCacheStore = <TParams extends unknown[], TData>(
    name: string,
    fetcher: (...params: TParams) => Promise<TData>,
    {
        ttlMs = FIVE_MINUTES_MS,
        maxEntries = DEFAULT_MAX_ENTRIES,
    }: CacheStoreOptions = {},
): CacheStoreApi<TParams, TData> => {
    const useCacheStore = create<
        CacheState<TData> & CacheInternalActions<TData>
    >()(
        devtools(
            (set) => ({
                entries: {},

                patchEntry: (key, patch): void =>
                    set(
                        (state) => ({
                            entries: {
                                ...state.entries,
                                [key]: {
                                    ...state.entries[key],
                                    ...patch,
                                } as CacheEntry<TData>,
                            },
                        }),
                        undefined,
                        `${name}/patchEntry:${key}`,
                    ),

                removeEntry: (key): void =>
                    set(
                        (state) => {
                            const { [key]: _, ...rest } = state.entries;
                            return { entries: rest };
                        },
                        undefined,
                        `${name}/removeEntry:${key}`,
                    ),
            }),
            {
                name,
                enabled: checkDevEnv(),
                store: name,
            },
        ),
    );

    /*
     * Non-reactive bookkeeping, deliberately kept out of the store state:
     * - inflightByKey dedupes concurrent requests per key;
     * - latestFetchIdByKey discards results of requests that were
     *   invalidated (or superseded) while in flight;
     * - accessTimes drive LRU eviction without triggering re-renders.
     */
    const inflightByKey = new Map<string, Promise<TData | undefined>>();
    const latestFetchIdByKey = new Map<string, number>();
    const accessTimes = new Map<string, number>();
    let fetchIdCounter = 0;

    const getEntries = (): Record<string, CacheEntry<TData>> =>
        useCacheStore.getState().entries;

    const touchAccess = (key: string): void => {
        accessTimes.set(key, Date.now());
    };

    const dropKey = (key: string): void => {
        accessTimes.delete(key);
        inflightByKey.delete(key);
        latestFetchIdByKey.delete(key);
        useCacheStore.getState().removeEntry(key);
    };

    const evictIfNeeded = (): void => {
        const entries = getEntries();
        if (Object.keys(entries).length <= maxEntries) {
            return;
        }

        Object.keys(entries)
            .map((key) => ({
                key,
                accessedAt:
                    accessTimes.get(key) ?? entries[key]?.fetchedAt ?? 0,
            }))
            .sort((a, b) => a.accessedAt - b.accessedAt)
            .forEach(({ key }) => {
                if (Object.keys(getEntries()).length > maxEntries) {
                    dropKey(key);
                }
            });
    };

    const runFetch = async (
        key: string,
        params: TParams,
        background: boolean,
        fetchId: number,
    ): Promise<TData | undefined> => {
        try {
            if (background) {
                // Stale-while-revalidate: data stays visible, no spinner.
                if (getEntries()[key]?.error !== undefined) {
                    useCacheStore
                        .getState()
                        .patchEntry(key, { error: undefined });
                }
            } else {
                useCacheStore.getState().patchEntry(key, {
                    isLoading: true,
                    error: undefined,
                });
            }

            const data = await fetcher(...params);

            /*
             * Discard results of requests that were invalidated (or
             * superseded) while in flight — stale data must never
             * re-enter the cache through a resolved old request.
             */
            if (latestFetchIdByKey.get(key) !== fetchId) {
                return data;
            }

            const fetchedAt = Date.now();
            accessTimes.set(key, fetchedAt);
            useCacheStore.getState().patchEntry(key, {
                data,
                isLoading: false,
                error: undefined,
                fetchedAt,
            });
            inflightByKey.delete(key);
            evictIfNeeded();
            return data;
        } catch (err: unknown) {
            console.error('Operation failed:', err);

            if (latestFetchIdByKey.get(key) === fetchId) {
                /*
                 * Errors never replace data and never touch fetchedAt —
                 * a failed revalidation keeps the previous data visible
                 * and stays stale for the next read.
                 */
                useCacheStore.getState().patchEntry(key, {
                    isLoading: false,
                    error: toErrorMessage(err),
                });
                inflightByKey.delete(key);
            }
            return undefined;
        }
    };

    const startFetch = (
        key: string,
        params: TParams,
        background: boolean,
    ): Promise<TData | undefined> => {
        const existing = inflightByKey.get(key);
        if (existing) {
            return existing;
        }

        const fetchId = ++fetchIdCounter;
        latestFetchIdByKey.set(key, fetchId);
        const promise = runFetch(key, params, background, fetchId);
        inflightByKey.set(key, promise);
        return promise;
    };

    const getData = (...params: TParams): Promise<TData | undefined> => {
        const key = buildCacheKey(params);
        const entry = getEntries()[key];

        if (
            entry?.data !== undefined &&
            entry.fetchedAt !== undefined &&
            Date.now() - entry.fetchedAt < ttlMs
        ) {
            // Fresh hit — serve from the cache without a network call.
            touchAccess(key);
            return Promise.resolve(entry.data);
        }

        if (entry?.data !== undefined) {
            /*
             * Stale hit — resolve the stale data immediately and
             * revalidate in the background; subscribers see the fresh
             * data when the fetch resolves.
             */
            touchAccess(key);
            startFetch(key, params, true);
            return Promise.resolve(entry.data);
        }

        // Cache miss — fetch (foreground, with loading state).
        return startFetch(key, params, false);
    };

    const refresh = (...params: TParams): Promise<TData | undefined> => {
        const key = buildCacheKey(params);
        touchAccess(key);
        // Explicit fetch — always bypasses the freshness window.
        return startFetch(key, params, false);
    };

    const invalidate = (...params: TParams): void => {
        dropKey(buildCacheKey(params));
    };

    const invalidateAll = (): void => {
        Object.keys(getEntries()).forEach(dropKey);
    };

    const clear = (): void => {
        invalidateAll();
    };

    registerCacheClear(name, clear);

    return {
        useEntry: (...params: OptionalParams<TParams>): CacheEntry<TData> =>
            useCacheStore(
                (state) =>
                    state.entries[buildCacheKey(params)] ??
                    (EMPTY_ENTRY as CacheEntry<TData>),
            ),
        getEntry: (...params: OptionalParams<TParams>) =>
            getEntries()[buildCacheKey(params)],
        getData,
        refresh,
        invalidate,
        invalidateAll,
        clear,
    };
};
